import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ProgressiveChecklist from "../checks/ProgressiveChecklist";
import ReadyToLeavePanel from "../checks/ReadyToLeavePanel";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * DailyChecksGate — mounts on Work Mode entry. Auto-prompts the two daily checks
 * (vehicle then business) for premium+ instructors. Once both done (or skipped)
 * for today, never prompts again until tomorrow.
 *
 * The FINAL completion screen of the queue carries a "You are ready" panel
 * with first-lesson + leave-in, sourced from /api/work-mode/today-at-a-glance.
 * This turns the "wait mode" gap before leaving into a calm ready state.
 *
 * Basic-plan users: gate stays silent. They access checks manually via the
 * dashboard widget.
 */
export default function DailyChecksGate({ onAllComplete }) {
  const nav = useNavigate();
  const [activeCheck, setActiveCheck] = useState(null);
  const [queue, setQueue] = useState([]);
  const [catalogue, setCatalogue] = useState({});
  const [glance, setGlance] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const loadAndQueue = useCallback(async () => {
    try {
      const [statusRes, catRes, glanceRes] = await Promise.all([
        axios.get(`${API}/api/checks/today-status`, { withCredentials: true }),
        axios.get(`${API}/api/checks/catalogue`, { withCredentials: true }),
        axios
          .get(`${API}/api/work-mode/today-at-a-glance`, { withCredentials: true })
          .catch(() => ({ data: null })),
      ]);
      const status = statusRes.data;
      const cat = (catRes.data.checks || []).reduce((acc, c) => {
        acc[c.key] = c;
        return acc;
      }, {});
      setCatalogue(cat);
      setGlance(glanceRes.data || null);
      if (!status.should_auto_prompt) {
        setLoaded(true);
        return;
      }
      const todo = [];
      if (!status.daily_vehicle.completed && !status.daily_vehicle.skipped) todo.push("daily_vehicle");
      if (!status.daily_business.completed && !status.daily_business.skipped) todo.push("daily_business");
      setQueue(todo);
      if (todo.length > 0) setActiveCheck(cat[todo[0]] || null);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadAndQueue();
  }, [loadAndQueue]);

  const submit = async ({ skipped, answers }, checkKey) => {
    try {
      const { data } = await axios.post(
        `${API}/api/checks/submit`,
        { check_key: checkKey, skipped, answers },
        { withCredentials: true }
      );
      const streak = data?.streak?.streak;
      if (!skipped && streak && [7, 30, 100].includes(streak)) {
        toast.success(
          streak === 100
            ? "Elite Operator — 100 checks done."
            : streak === 30
            ? "30-day streak. Pro habits."
            : "7-day streak. Keep going."
        );
      }
    } catch {
      /* silent */
    }
  };

  const handleComplete = async (payload) => {
    const checkKey = activeCheck?.key;
    if (!checkKey) return;
    await submit(payload, checkKey);
  };

  const handleClose = () => {
    setQueue((prev) => {
      const next = prev.slice(1);
      if (next.length > 0) {
        setActiveCheck(catalogue[next[0]] || null);
      } else {
        setActiveCheck(null);
        onAllComplete?.();
      }
      return next;
    });
  };

  if (!loaded || !activeCheck) return null;

  // Is this the last check in the queue? If so, we render the calm
  // ready-to-leave panel + warmer wording on the completion screen.
  const isLast = queue.length <= 1;
  const next = glance?.next_lesson || null;
  const leaveIn = next?.minutes_until;
  const tight = leaveIn !== null && leaveIn !== undefined && leaveIn <= 25;

  // Custom completion title/subtitle ONLY for the final daily check.
  const titleOverride = isLast
    ? leaveIn !== null && leaveIn !== undefined
      ? "You are ready"
      : (glance?.lessons_today || 0) === 0
      ? "You are ready · clear diary"
      : "You are ready"
    : null;

  const subtitleOverride = isLast
    ? tight
      ? "Heads up — leave shortly. Drive calm."
      : ""
    : null;

  // Override CTA so the final screen sends them straight into Work Mode flow.
  const ctaOverride = isLast
    ? { cta_label: "Open Work Mode", cta_route: "/work-mode" }
    : null;

  const enrichedCheck = ctaOverride
    ? { ...activeCheck, ...ctaOverride }
    : activeCheck;

  const extras = isLast ? (
    <ReadyToLeavePanel
      lessonsToday={glance?.lessons_today || 0}
      nextLesson={next}
      earningsToday={glance?.earnings_today || 0}
      tone={tight ? "tight" : "calm"}
    />
  ) : null;

  return (
    <ProgressiveChecklist
      check={enrichedCheck}
      onComplete={handleComplete}
      onClose={handleClose}
      onCta={() => {
        // Final CTA sends them directly into Work Mode.
        if (isLast && ctaOverride) {
          nav(ctaOverride.cta_route);
        }
        handleClose();
      }}
      extraCompletionNode={extras}
      completionTitleOverride={titleOverride}
      completionSubtitleOverride={subtitleOverride}
      autoCloseMs={isLast ? 6000 : 1800}
    />
  );
}
